import {
  fetch_all_assigned_onboarding_cases,
  fetch_application_transition_history,
  fetch_queue_onboarding_cases,
  build_case_form_sections,
  build_transition_stage_timeline,
  build_summary_rows,
  fetch_configured_case_gate_ids,
  hydrate_case_detail
} from '@/services/onboarding_journey_service';
import { get_case_entity_type_id } from './manage_verification_helpers';
import {
  build_binding_options,
  build_case_detail_checks,
  build_load_promises,
  get_case_forms,
  replace_case_checks,
  update_all_filter_count
} from './manage_verification_load_helpers';
import {
  apply_case_manual_binding_fallback,
  build_manual_runtime_checks_for_cases,
  build_initial_case_checks,
  build_manual_mapping_by_link,
  check_has_manual_mappings,
  enrich_manual_checks_with_run_mappings
} from './manage_verification_manual_runtime_helpers';

function normalize_text(value = '') {
  return String(value || '').trim().toLowerCase();
}

function matches_active_journey(case_item = {}, journey_filter = '') {
  const normalized_filter = normalize_text(journey_filter);
  if (!normalized_filter) return true;

  const candidate_values = [
    case_item?.entity_type_id,
    case_item?.journey_tone,
    case_item?.journey_key,
    case_item?.journey
  ];
  return candidate_values.some((value) => normalize_text(value) === normalized_filter);
}

function get_session_role() {
  if (typeof window === 'undefined') return '';
  return window.sessionStorage.getItem('current_role') || '';
}

function is_entity_role(vm) {
  const role = normalize_text(vm?.$store?.getters?.currentRole || get_session_role());
  return role === 'entity';
}

async function fetch_verification_cases(vm) {
  if (is_entity_role(vm)) {
    return fetch_all_assigned_onboarding_cases({
      _: Date.now(),
      include_runtime_progress: false
    });
  }
  return fetch_queue_onboarding_cases({
    include_runtime_progress: false
  });
}

export const manageVerificationLoadMethods = {
  async load_checks() {
    this.is_loading_checks = true;
    this.checks_error = '';
    this.show_verification_system_loader();

    try {
      const cases = await fetch_verification_cases(this);
      const scoped_cases = cases.filter((case_item) => matches_active_journey(case_item, this.active_journey_filter));
      this.checks = await build_initial_case_checks(
        scoped_cases,
        this.verification_workspace_mode,
        { include_runtime_checks: false }
      );
      this.filters = update_all_filter_count(this.filters, this.case_rows.length);
      await this.sync_selected_case_from_route();
    } catch (error) {
      this.checks = [];
      this.checks_error = error?.response?.data?.message || error?.message || 'Unable to load verification checks.';
    } finally {
      this.is_loading_checks = false;
      if (!this.is_detail_page) {
        this.hide_verification_system_loader_after_render();
      }
    }
  },
  async open_case_summary(row_item = {}) {
    const case_item = row_item?.case_item;
    if (!case_item) return;

    this.show_verification_system_loader('Loading verification drawer...');
    this.reset_workflow_action_state();
    this.selected_case_summary = case_item;
    this.active_summary_detail_tab = 'summary';
    this.is_summary_detail_fullscreen = false;
    this.summary_rows = build_summary_rows(case_item);
    this.summary_stage_timeline = [];
    this.summary_case_form_sections = build_case_form_sections(case_item);
    this.summary_detail_tabs = this.build_summary_detail_tabs(this.summary_case_form_sections);
    this.is_loading_summary_detail = true;

    try {
      const [hydrated_case, configured_gate_ids, transition_history] = await Promise.all([
        hydrate_case_detail(case_item),
        fetch_configured_case_gate_ids(case_item).catch(() => []),
        fetch_application_transition_history(Number(case_item?.application_id || case_item?.id || 0)).catch(() => [])
      ]);
      this.selected_case_summary = hydrated_case;
      this.summary_rows = build_summary_rows(hydrated_case);
      this.summary_stage_timeline = build_transition_stage_timeline(
        hydrated_case,
        transition_history,
        configured_gate_ids
      );
      this.summary_case_form_sections = build_case_form_sections(hydrated_case);
      this.summary_detail_tabs = this.build_summary_detail_tabs(this.summary_case_form_sections);
      await this.load_workflow_actions_for_case(hydrated_case);
    } catch {
      this.selected_case_summary = case_item;
      await this.load_workflow_actions_for_case(case_item);
    } finally {
      this.is_loading_summary_detail = false;
      this.hide_verification_system_loader_after_render();
    }
  },
  async hydrate_verification_detail_checks(case_item = {}) {
    const entity_profile_id = Number(case_item?.entity_profile_id || case_item?.profile?.id || 0);
    const forms = get_case_forms(case_item);
    const entity_type_id = get_case_entity_type_id(case_item, case_item);
    const binding_options = build_binding_options(case_item);
    const fetched_data = await Promise.all(build_load_promises(
      entity_profile_id,
      entity_type_id,
      binding_options,
      forms
    ));
    fetched_data[1] = apply_case_manual_binding_fallback(fetched_data[1], case_item);
    const manual_mapping_by_link = await build_manual_mapping_by_link(
      fetched_data[1]?.manual_bindings
    );
    const checks = build_case_detail_checks(case_item, forms, fetched_data, manual_mapping_by_link);
    const runtime_checks = await build_manual_runtime_checks_for_cases([case_item]);
    const enriched_checks = await enrich_manual_checks_with_run_mappings(checks);
    const enriched_runtime_checks = await enrich_manual_checks_with_run_mappings(runtime_checks);
    const existing_manual_checks = this.checks.filter((check) => (
      check?.verification_mode === 'manual' && this.matches_case(check?.case_item, case_item)
    ));
    const enriched_existing_manual_checks = await enrich_manual_checks_with_run_mappings(existing_manual_checks);
    const effective_checks = enriched_checks.some(check_has_manual_mappings)
      ? [...enriched_checks, ...enriched_runtime_checks]
      : [...enriched_checks, ...enriched_runtime_checks, ...enriched_existing_manual_checks];

    this.checks = replace_case_checks(this.checks, effective_checks, case_item, this.matches_case);
  }
};
